/**
 * Apply Button Component
 * Large apply button with tracking and state management
 */

import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { trackJobApply } from '../../../../../utils/analytics/analytics';
import type { DisplayJob } from '../../../../../types';

export interface ApplyButtonProps {
  applyUrl: string;
  isApplied: boolean;
  displayJob: DisplayJob | null;
  applyBtnStyle: React.CSSProperties;
  applyBtnHoverStyle: React.CSSProperties;
  effectiveBrandColour: string;
  onSetApplied: (job: any, applied: boolean) => Promise<void>;
  /** Tighter spacing when placed high on the page (above the fold). */
  compact?: boolean;
  /** Second CTA after long content — uses a div wrapper so we do not duplicate a navigation landmark. */
  isSecondary?: boolean;
  /** When provided, renders a secondary "Tailor my CV" action below the apply link. */
  onTailorCv?: () => void;
  /** SRP card + job detail page redesign A/B bucket, see utils/analytics/card-design-variant.ts. */
  cardDesignVariant?: 'old' | 'new';
}

export const ApplyButton: React.FC<ApplyButtonProps> = ({
  applyUrl,
  isApplied,
  displayJob,
  applyBtnStyle,
  applyBtnHoverStyle,
  effectiveBrandColour,
  onSetApplied,
  compact = false,
  isSecondary = false,
  onTailorCv,
  cardDesignVariant,
}) => {
  const intl = useIntl();

  const handleClick = () => {
    // Single conversion event per apply click (job_apply_click was removed as
    // a duplicate of job_apply_conversion — same click, same params).
    if (displayJob) {
      trackJobApply({
        jobId: displayJob.objectID,
        jobTitle: displayJob.normalisedTitle || displayJob.title || '',
        companyId: displayJob.companyId || '',
        source: 'apply_cta',
        locale: intl.locale,
        cardDesignVariant,
        method: 'external_link',
      });
    }

    if (displayJob) {
      void onSetApplied({ ...displayJob, id: displayJob.objectID }, !isApplied);
    }
  };

  const link = (
    <a
      href={applyUrl}
      className={
        'w-full px-6 rounded-2xl text-white font-semibold shadow transition-transform duration-200 flex items-center justify-center text-center hover:scale-[1.02] active:scale-[0.98] ' +
        (compact ? 'py-3.5 text-base my-0 mb-5' : 'py-4 text-lg my-6')
      }
      style={applyBtnStyle}
      target="_blank"
      rel="noopener noreferrer"
      onMouseOver={effectiveBrandColour ? (e) => Object.assign(e.currentTarget.style, applyBtnHoverStyle) : undefined}
      onMouseOut={effectiveBrandColour ? (e) => Object.assign(e.currentTarget.style, applyBtnStyle) : undefined}
      onClick={handleClick}
    >
      {isApplied ? (
        <FormattedMessage
          id="jobDetailPage.applied"
          defaultMessage="Applied ✓"
          description="Button text when job has been applied to"
        />
      ) : (
        <FormattedMessage id="jobDetailPage.applyNow" defaultMessage="Apply on Company Website" description="Apply now button on job detail page" />
      )}
    </a>
  );

  const tailorCvButton = onTailorCv ? (
    <button
      type="button"
      onClick={onTailorCv}
      className={`w-full text-center text-sm font-medium text-current opacity-80 hover:opacity-100 underline underline-offset-2 cursor-pointer ${compact ? '-mt-4 mb-5' : '-mt-4 mb-6'}`}
    >
      <FormattedMessage
        id="jobDetailPage.tailorCvCta"
        defaultMessage="✨ Tailor my CV for this job"
        description="Secondary action below the apply button offering to tailor the user's CV to this job"
      />
    </button>
  ) : null;

  if (isSecondary) {
    return (
      <div className="w-full">
        {link}
        {tailorCvButton}
      </div>
    );
  }

  return (
    <nav
      id="job-apply-cta"
      aria-label={intl.formatMessage({ id: 'jobDetailPage.applyNavigation', defaultMessage: 'Apply to job', description: 'Accessibility label for navigation to job application page' })}
    >
      {link}
      {tailorCvButton}
    </nav>
  );
};
